import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  ShieldCheck,
  Clock,
  Eye,
  Shield,
  Calculator,
  Lock,
  Database,
  Bell,
  AlertTriangle,
  KeyRound,
} from 'lucide-react';
import { SecurityBadge } from '../SecureMessage';
import { PanicButton } from '../PanicButton';
import { ServerUrlSetting } from '../ServerUrlSetting';
import { LocalDataInspector } from '../LocalDataInspector';
import { PushNotificationSetting } from '../PushNotificationSetting';
import { BlockedUsersList } from '../BlockedUsersList';
import { DeleteAccountSetting } from '../DeleteAccountSetting';
import { ContactTagSetting } from '../ContactTagSetting';
import { KeyTransparencyDialog } from '../KeyTransparencyDialog';
import { KeySyncSetting } from '../KeySyncSetting';
import { WarrantCanaryChip } from '../WarrantCanary';
import { DuressPasswordSetting } from '../DuressPasswordSetting';
import { WelcomeTemplateSetting } from '../WelcomeTemplateSetting';
import { KeyRotationSetting } from '../KeyRotationSetting';
import { SecurityHaloCard } from '../SecurityHaloCard';
import { RotationForecastCard } from '../RotationForecastCard';
import { DefaultTtlSetting } from '../DefaultTtlSetting';

/**
 * SettingsDialog
 *
 * Tab-ified into four sections so the panel stays scannable as the feature
 * surface grows:
 *   - Privacy:       account, anti-surveillance, encryption, retention, security level
 *   - Data:          backend server URL, local data inspector (audit/restore)
 *   - Notifications: push notifications, blocked users
 *   - Emergency:     decoy mode, panic button
 *
 * `scrollTo="local-data"` (from the Cmd+K "Local data inspector" tile) jumps
 * straight to the Data tab and scrolls the inspector into view.
 */
export const SettingsDialog = ({
  isOpen,
  onClose,
  securityStatus,
  onActivateDecoy,
  scrollTo,
  contacts = [],
  conversations = [],
}) => {
  const { user } = useAuth();
  const localDataRef = useRef(null);
  const pushRef = useRef(null);
  const blockedRef = useRef(null);
  const emergencyRef = useRef(null);
  const haloRef = useRef(null);
  const [tab, setTab] = useState('privacy');
  const [selfKeyHistoryOpen, setSelfKeyHistoryOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Deep-link: Cmd+K action tiles pass a `scrollTo` identifier so we can
    // land the user on the right tab AND the right row inside it without
    // them navigating. Map identifier → target tab; scroll to ref after
    // the tab has rendered.
    const TAB_BY_SCROLL_TARGET = {
      'local-data': 'data',
      'push': 'notifications',
      'blocked-users': 'notifications',
      'emergency': 'emergency',
      'security-halo': 'privacy',
    };
    const nextTab = TAB_BY_SCROLL_TARGET[scrollTo];
    if (!nextTab) return;
    setTab(nextTab);
    const t = setTimeout(() => {
      const ref =
        scrollTo === 'local-data'
          ? localDataRef.current
          : scrollTo === 'push'
          ? pushRef.current
          : scrollTo === 'blocked-users'
          ? blockedRef.current
          : scrollTo === 'emergency'
          ? emergencyRef.current
          : scrollTo === 'security-halo'
          ? haloRef.current
          : null;
      ref?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 180);
    return () => clearTimeout(t);
  }, [isOpen, scrollTo]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>Manage your account and security</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList className="grid grid-cols-4 w-full" data-testid="settings-tabs">
            <TabsTrigger value="privacy" data-testid="settings-tab-privacy" className="text-xs">
              <Lock className="w-3.5 h-3.5 mr-1" />
              Privacy
            </TabsTrigger>
            <TabsTrigger value="data" data-testid="settings-tab-data" className="text-xs">
              <Database className="w-3.5 h-3.5 mr-1" />
              Data
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-tab-notifications" className="text-xs">
              <Bell className="w-3.5 h-3.5 mr-1" />
              Notify
            </TabsTrigger>
            <TabsTrigger value="emergency" data-testid="settings-tab-emergency" className="text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" />
              Emergency
            </TabsTrigger>
          </TabsList>

          {/* ─── Privacy ─── */}
          <TabsContent value="privacy" className="space-y-6 mt-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Account</label>
              <div className="p-4 rounded-lg bg-secondary/30">
                <p className="font-medium text-white">{user?.username}</p>
                <p className="text-sm text-slate-400">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">
                Warrant canary
              </label>
              <div className="p-4 rounded-lg bg-secondary/30 space-y-2">
                <WarrantCanaryChip />
                <p className="text-xs text-slate-500">
                  An ed25519-signed statement that the operator of this
                  deployment has not received a gag order, subpoena, or
                  key-disclosure demand. Automatically re-signed every 24
                  hours — if the signature goes stale, treat the canary as
                  dead. Click the chip to see the full statement, signature,
                  and public-key fingerprint.
                </p>
              </div>
            </div>

            <DuressPasswordSetting />

            <WelcomeTemplateSetting />

            <KeyRotationSetting />

            <div ref={haloRef}>
              <SecurityHaloCard contacts={contacts} conversations={conversations} />
            </div>

            <RotationForecastCard />

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Anti-Surveillance Protection</label>
              <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
                <div className="flex items-center gap-2 text-emerald-400">
                  <Eye className="w-5 h-5" />
                  <span className="font-medium">Hover-to-Reveal Active</span>
                </div>
                <p className="text-sm text-slate-500">
                  Messages are blurred until you hover over them. This prevents automated screen
                  capture and surveillance software from reading your messages.
                </p>
                <div className="flex items-center gap-2 text-blue-400 mt-2">
                  <Shield className="w-5 h-5" />
                  <span className="font-medium">Window Focus Protection</span>
                </div>
                <p className="text-sm text-slate-500">
                  Content is automatically hidden when the window loses focus, preventing
                  background screen recording.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Encryption Status</label>
              <div className="p-4 rounded-lg bg-secondary/30">
                <div className="flex items-center gap-2 text-emerald-400">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="font-medium">End-to-end encryption active</span>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  RSA-OAEP + AES-GCM encryption. Keys stored locally, server cannot read messages.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3"
                  onClick={() => setSelfKeyHistoryOpen(true)}
                  data-testid="open-self-key-history"
                >
                  <KeyRound className="w-3.5 h-3.5 mr-1.5" />
                  View my key rotation history
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-3 ml-2 text-slate-400 hover:text-slate-100"
                  onClick={() => {
                    try {
                      window.localStorage.removeItem('ghostcomm_onboarded');
                    } catch {
                      /* private mode */
                    }
                    onClose?.();
                    // Force the tour to re-mount by triggering a reload.
                    window.location.reload();
                  }}
                  data-testid="replay-onboarding-tour"
                >
                  Replay welcome tour
                </Button>
              </div>
            </div>

            <DefaultTtlSetting />

            {securityStatus && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Current Security Level</label>
                <SecurityBadge status={securityStatus} />
              </div>
            )}

            <div className="pt-2 border-t border-slate-800">
              <ContactTagSetting />
            </div>
          </TabsContent>

          {/* ─── Data ─── */}
          <TabsContent value="data" className="space-y-6 mt-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Server</label>
              <ServerUrlSetting />
            </div>
            <div className="space-y-2" ref={localDataRef}>
              <label className="text-sm text-slate-400">Local Data</label>
              <LocalDataInspector />
            </div>
            <KeySyncSetting />
          </TabsContent>

          {/* ─── Notifications ─── */}
          <TabsContent value="notifications" className="space-y-6 mt-4">
            <div className="space-y-2" ref={pushRef}>
              <label className="text-sm text-slate-400">Push notifications</label>
              <PushNotificationSetting />
            </div>
            <div ref={blockedRef}>
              <BlockedUsersList />
            </div>
          </TabsContent>

          {/* ─── Emergency ─── */}
          <TabsContent value="emergency" className="space-y-6 mt-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Decoy Mode</label>
              <div className="p-4 rounded-lg bg-secondary/30">
                <Button
                  variant="outline"
                  className="w-full border-zinc-600 hover:bg-zinc-700"
                  onClick={() => {
                    onClose();
                    onActivateDecoy();
                  }}
                  data-testid="activate-decoy-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Activate Decoy (Calculator)
                </Button>
                <p className="text-xs text-slate-500 mt-3">
                  Shows a fake calculator app. To return: type{' '}
                  <span className="font-mono bg-zinc-700 px-1 rounded">1337</span> then press{' '}
                  <span className="font-mono bg-zinc-700 px-1 rounded">=</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Keyboard shortcut:{' '}
                  <span className="font-mono bg-zinc-700 px-1 rounded">Ctrl+Shift+D</span>
                </p>
              </div>
            </div>

            <div className="space-y-2" ref={emergencyRef}>
              <label className="text-sm text-slate-400">Emergency Actions</label>
              <PanicButton onActivate={() => onClose()} />
              <p className="text-xs text-slate-500 mt-2">
                Instantly wipes all local data including encryption keys. Use only in emergencies.
              </p>
            </div>

            <div className="space-y-2 pt-4 border-t border-border">
              <label className="text-sm text-slate-400">Danger zone</label>
              <DeleteAccountSetting />
              <p className="text-xs text-slate-500">
                Removes your account, sent messages, invite codes, and membership in every
                conversation. Cannot be undone. Required for GDPR / CCPA right-to-erasure.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
      {/* Self key history — reachable from the Privacy tab */}
      <KeyTransparencyDialog
        isOpen={selfKeyHistoryOpen}
        onClose={() => setSelfKeyHistoryOpen(false)}
        peer={user ? { id: user.id, username: user.username } : null}
        isSelf
      />
    </Dialog>
  );
};
